/**
 * What the saved grids actually cost and actually returned.
 *
 * Pure computation over what is already stored: no estimate, no projection. A grid
 * counts as settled once the result of its draw is known; anything still pending is
 * kept out of the return rate so that a draw not yet held cannot flatter it.
 *
 * The point of this is to replace recollection with arithmetic. Wins are memorable
 * and losses are not, which is exactly the asymmetry a running total removes.
 */
import { GAMES, matchGrid, rankFor } from '../engine';
import type { GameKey } from '../engine';
import type { DrawRecord, PayoutRow } from './api';
import type { SavedGrid } from './storage';

export interface SettledGrid {
  grid: SavedGrid;
  /** Prize in cents. 0 when the grid won nothing. */
  wonCents: number;
  stakeCents: number;
  rank: number | null;
  rankLabel: string | null;
  matchedMain: number[];
  matchedBonus: number[];
}

export interface Ledger {
  /** Grids whose draw has been held and whose result is known. */
  settled: SettledGrid[];
  /** Grids waiting for their draw. Counted in `pendingStakeCents`, nowhere else. */
  pending: SavedGrid[];
  stakeCents: number;
  wonCents: number;
  pendingStakeCents: number;
  /** wonCents / stakeCents, or null while nothing is settled. */
  returnRate: number | null;
  winningGrids: number;
  byGame: Record<GameKey, { stakeCents: number; wonCents: number; settled: number }>;
}

export interface LedgerInputs {
  /** Draw results by game, then by ISO date. */
  results: Partial<Record<GameKey, Map<string, DrawRecord>>>;
  /** Payout tables by game, then by ISO date. */
  payouts: Partial<Record<GameKey, Record<string, PayoutRow[]>>>;
}

export function buildLedger(grids: readonly SavedGrid[], inputs: LedgerInputs): Ledger {
  const settled: SettledGrid[] = [];
  const pending: SavedGrid[] = [];
  const byGame = {} as Ledger['byGame'];
  for (const key of Object.keys(GAMES) as GameKey[]) {
    byGame[key] = { stakeCents: 0, wonCents: 0, settled: 0 };
  }

  for (const saved of grids) {
    const g = GAMES[saved.game];
    const stakeCents = Math.round(g.price * 100);
    const result = inputs.results[saved.game]?.get(saved.drawDate);

    if (result === undefined) {
      pending.push(saved);
      continue;
    }

    const m = matchGrid(
      { main: saved.main, bonus: saved.bonus },
      { main: result.main, bonus: result.bonus },
      g,
    );
    const spec = rankFor(saved.game, m.mainHits, m.bonusHits);

    let wonCents = 0;
    if (spec !== null) {
      const row = inputs.payouts[saved.game]?.[saved.drawDate]?.find((p) => p.rank === spec.rank);
      // A known rank with an unknown amount stays at zero rather than guessed: the
      // running total must never contain a number nobody published.
      wonCents = row?.amountCents ?? 0;
    }

    settled.push({
      grid: saved,
      wonCents,
      stakeCents,
      rank: spec?.rank ?? null,
      rankLabel: spec?.label ?? null,
      matchedMain: m.matchedMain,
      matchedBonus: m.matchedBonus,
    });

    byGame[saved.game].stakeCents += stakeCents;
    byGame[saved.game].wonCents += wonCents;
    byGame[saved.game].settled += 1;
  }

  const stakeCents = settled.reduce((s, x) => s + x.stakeCents, 0);
  const wonCents = settled.reduce((s, x) => s + x.wonCents, 0);
  const pendingStakeCents = pending.reduce(
    (s, x) => s + Math.round(GAMES[x.game].price * 100),
    0,
  );

  return {
    settled,
    pending,
    stakeCents,
    wonCents,
    pendingStakeCents,
    returnRate: stakeCents > 0 ? wonCents / stakeCents : null,
    winningGrids: settled.filter((x) => x.wonCents > 0).length,
    byGame,
  };
}

/**
 * Expected return over the same stake, from the measured RTP of each game.
 * Shown next to the observed rate so a small sample is read as what it is.
 */
export function expectedWonCents(ledger: Ledger): number {
  let total = 0;
  for (const key of Object.keys(ledger.byGame) as GameKey[]) {
    total += ledger.byGame[key].stakeCents * GAMES[key].measuredRtp;
  }
  return Math.round(total);
}
